import * as Yup from 'yup';
import { useState, useEffect } from 'react';
import { useSnackbar } from 'notistack';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
// hooks
import useAuth from '../../../hooks/useAuth';
// components
import { FormProvider, RHFTextField, RHFFileUploadField } from '../../../components/hook-form';
// util
import axios from '../../../utils/axios';
import {default as axiosNormal} from 'axios';

export default function ContractInfoDialog({ isOpen, closeDialog, goAccept }) {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [contractInfo, setContractInfo] = useState({});
  const { enqueueSnackbar } = useSnackbar();

  const UpdateCompanySchema = Yup.object().shape({
    reprName: Yup.string().required('대표자명을 입력하세요'),
    businessRegistrationNumber: Yup.string().matches(/^[\d-]*$/, '올바른 사업자등록번호를 입력하세요').required('사업자등록번호를 입력하세요'),
    address: Yup.string().required('주소를 입력하세요'),
    certificateFile: Yup.mixed().test("fileSize", "파일을 선택하세요", (value) => {
      return value.id || value.size > 0;
    }),
  });

  const defaultValues = {
    reprName: contractInfo?.repr_name || '',
    businessRegistrationNumber: contractInfo?.business_registration_number || '',
    address: contractInfo?.address || '',
    certificateFile: contractInfo?.files?.[0] || {name: '', file: ''},
  };

  const methods = useForm({
    resolver: yupResolver(UpdateCompanySchema),
    defaultValues,
  });

  useEffect(() => {
    reset({
      reprName: contractInfo?.repr_name || '',
      businessRegistrationNumber: contractInfo?.business_registration_number || '',
      address: contractInfo?.address || '',
      certificateFile: contractInfo?.files?.[0] || {name: '', file: ''},
    });
  }, [contractInfo]);

  const {
    reset,
    handleSubmit,
    getValues,
  } = methods;

  const getContractInfo = async () => {
    setIsLoading(true);
    const response = await axios.get(`api/company/${user?.employee.company[0].id}/settings/`);
    setContractInfo(response.data)
    setIsLoading(false);
  }

  const fileUploadBeforeSubmit = async (file) => {
    if (file.id && file.name) {
      return [{id: file.id, file: file.name, name: file.name}]
    } else if (file.name === '') {
      return []
    } else {
      const fileURLResponse = await axios.post(
        `/api/company/${user?.employee.company[0].id}/settings/files/upload-url/`,
        {filename: file.name}
      );
      const {filename, url} = fileURLResponse.data;

      await axiosNormal.put(
        url,
        file,
        {
          headers: {
            'Content-Type': file.type
          }
        }
      );
      return [{file: filename, name: file.name}];
    }
  };

  const closeThis = async () => {
    reset({
      reprName: contractInfo?.repr_name || '',
      businessRegistrationNumber: contractInfo?.business_registration_number || '',
      address: contractInfo?.address || '',
      certificateFile: contractInfo?.files?.[0] || {name: '', file: ''},
    });
    closeDialog();
  }

  useEffect(() => {
    const initialize = async () => {
      if (user) {
        await getContractInfo();
      }
    };

    initialize();
  }, [user]);

  const onSubmit = async (formData) => {
    try {
      setIsLoading(true);
      const fileUploadedData = await fileUploadBeforeSubmit(formData.certificateFile);
      const patchData = {
        repr_name: formData.reprName,
        business_registration_number: formData.businessRegistrationNumber,
        address: formData.address,
        files: fileUploadedData,
      }
      await axios.patch(
        `api/company/${user?.employee.company[0].id}/contract-info/`,
        patchData,
      )
      enqueueSnackbar('저장되었습니다')
      setIsLoading(false);
      goAccept();
    } catch (err) {
      enqueueSnackbar('회사정보 저장에 오류가 발생하였습니다. 관리자에 문의 바랍니다', { variant: 'error' });
      console.error(err);
      setIsLoading(false);
    }
  }

  const openFile = async (item) => {
    const response = await axios.post(`/api/company/${user?.employee.company[0].id}/settings/files/${item.id}/url/`);
    window.open(response.data.url);
  };

  return (
    <Dialog maxWidth="sm" open={isOpen} onClose={closeThis}>
      <DialogTitle sx={{ mb: 2 }}>
        계약을 진행하기 전에, 세금계산서 발급에 필요한 회사 정보를 입력해주세요.
      </DialogTitle>
      <DialogContent>
        <DialogContentText paragraph>
          사업자 등록 번호가 없으시면 본인 주민등록번호를 입력하시고, 사업자등록증 란에 신분증을 올려주세요.
        </DialogContentText>
        <FormProvider methods={methods}>
          <Box
            sx={{
              my: 1,
              display: 'grid',
              rowGap: 3,
              columnGap: 2,
              gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(1, 1fr)' },
            }}
          >
            <RHFTextField
              name="reprName"
              label="대표자명"
            />
            <RHFTextField
              name="businessRegistrationNumber"
              label="사업자 등록 번호"
            />
            <RHFTextField
              name="address"
              label="회사주소"
              />
            <RHFFileUploadField
              link
              name="certificateFile"
              label="사업자등록증"
              item={{file: getValues("certificateFile")}}
              openfile={() => openFile(getValues("certificateFile"))}
              helperText="파일을 선택하고 아래 저장하기를 눌러주세요"
            />
            </Box>
        </FormProvider>
      </DialogContent>
      <DialogActions>
        <LoadingButton
          onClick={closeThis}
          loading={isLoading}
        >
          취소
        </LoadingButton>
        <LoadingButton
          type="button"
          variant="contained"
          onClick={handleSubmit(onSubmit)}
          loading={isLoading}
        >
          저장하기
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}